import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/switch/switch.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/radio/radio.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '../components/shop-day-bar';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { ApiFactory } from '../api/easypos-api';
import {
  IActiveOrderSummary,
  RefundMethod,
  SaleStatus,
} from '../api/model/requests';
import { resolveRouterPath, router } from '../router';
import { styles as sharedStyles } from '../styles/shared-styles';
import { getOrCreateDeviceId } from '../datatypes/device';

const POLL_MS = 5000;

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

// Bar / kitchen / barista board.
//
// Three columns, in operator priority: Paid → Preparing → Ready. Each card
// shows the customer label (pickup code, table tag, paid-time fallback),
// item count, total, channel, and a thin workflow footer that advances or
// reverts the order's status. Tapping the card body navigates to the
// full-page detail view at /orders/active/:id.
@customElement('active-orders-page')
export class ActiveOrdersPage extends Base {
  @state() private _orders: IActiveOrderSummary[] = [];
  @state() private _error: string | null = null;
  @state() private _loading = true;

  // Wait-time thresholds (minutes). Loaded from the shop's settings;
  // 10/20 are the server-side defaults if the call fails.
  @state() private _warnMinutes = 10;
  @state() private _dangerMinutes = 20;
  // Tick state — bumps once a minute so card ages re-render without
  // waiting for the next poll.
  @state() private _now = Date.now();

  // Cancel-order flow.
  @state() private _cancelOrder: IActiveOrderSummary | null = null;
  @state() private _cancelRefund = true;
  @state() private _cancelMethod: RefundMethod = 'cash';
  @state() private _cancelReason = '';
  // Second-confirm gate when the operator chooses to skip the refund.
  @state() private _cancelSkipConfirm = false;
  @state() private _cancelBusy = false;

  private _pollTimer?: number;
  private _tickTimer?: number;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        max-width: 1200px;
        margin: 0 auto;
        padding: 16px;
        box-sizing: border-box;
      }
      h2 {
        margin: 0 0 12px 0;
      }
      .board {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 12px;
      }
      @media (max-width: 800px) {
        .board {
          grid-template-columns: 1fr;
        }
      }
      .col {
        display: flex;
        flex-direction: column;
        gap: 8px;
        background: var(--sl-color-neutral-50);
        padding: 8px;
        border-radius: var(--sl-border-radius-medium);
        min-height: 200px;
      }
      .col-title {
        /* This is an h2 for semantics, so the browser's heading margins have to go or the
           columns gain space they never had. Every other value here already overrides the
           heading defaults; only the margin did not. */
        margin: 0;
        font-weight: 700;
        text-transform: uppercase;
        font-size: 0.85rem;
        opacity: 0.7;
      }
      .order-card {
        cursor: pointer;
        transition: transform 0.05s ease;
      }
      .order-card:hover {
        transform: translateY(-1px);
      }
      /* Phase Y — cashier-visible order number. Sits on the top-left
         of every card so the operator can yell "#42 ready" without
         hunting for the customer name. Mono for the digits; large
         enough to read from the bar. */
      .order-card .order-number {
        font-family: var(
          --font-mono,
          ui-monospace,
          SFMono-Regular,
          Menlo,
          monospace
        );
        font-weight: 700;
        font-size: 1.5rem;
        line-height: 1;
        letter-spacing: -0.02em;
        color: var(--app-text);
        font-variant-numeric: tabular-nums;
      }
      .order-card .order-number-hash {
        opacity: 0.45;
        margin-right: 1px;
      }
      .order-card .order-fallback-id {
        font-family: var(
          --font-mono,
          ui-monospace,
          SFMono-Regular,
          Menlo,
          monospace
        );
        font-size: 0.85rem;
        opacity: 0.6;
      }
      .row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
      }
      .age {
        font-variant-numeric: tabular-nums;
      }
      .age.green {
        color: var(--sl-color-success-600);
      }
      .age.amber {
        color: var(--sl-color-warning-600);
        font-weight: 600;
      }
      .age.red {
        color: var(--sl-color-danger-600);
        font-weight: 700;
      }
      .empty {
        font-style: italic;
        opacity: 0.6;
        text-align: center;
        padding: 24px 8px;
      }
      .workflow-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        margin-top: 10px;
        padding-top: 8px;
        border-top: 1px solid var(--sl-color-neutral-100);
      }
      .workflow-footer sl-button::part(base) {
        min-height: 28px;
      }
      /* Subtle, low-emphasis cancel so it doesn't compete with the
         primary advance action and is harder to mis-tap. */
      .workflow-footer .cancel-cta::part(base) {
        color: var(--sl-color-danger-600);
        border-color: transparent;
        background: transparent;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    this._pollTimer = window.setInterval(() => void this._refresh(), POLL_MS);
    this._tickTimer = window.setInterval(() => {
      this._now = Date.now();
    }, 60_000);
  }

  disconnectedCallback() {
    if (this._pollTimer !== undefined) clearInterval(this._pollTimer);
    if (this._tickTimer !== undefined) clearInterval(this._tickTimer);
    super.disconnectedCallback();
  }

  firstUpdated() {
    void this._firstUpdatedAsync();
  }

  private async _firstUpdatedAsync() {
    await Promise.all([this._refresh(), this._loadShopSettings()]);
    this._loading = false;
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('activeShop') && this.activeShop) {
      void this._refresh();
      void this._loadShopSettings();
    }
  }

  private async _loadShopSettings() {
    if (!this.activeShop) return;
    const api = ApiFactory.authenticated();
    const r = await api.getShop(this.activeShop.shopName);
    if (!r.success || !r.data) return;
    if (typeof r.data.orderWaitWarnMinutes === 'number')
      this._warnMinutes = r.data.orderWaitWarnMinutes;
    if (typeof r.data.orderWaitDangerMinutes === 'number')
      this._dangerMinutes = r.data.orderWaitDangerMinutes;
  }

  private async _refresh() {
    if (!this.activeShop) {
      this._error = 'No active shop.';
      return;
    }
    const api = ApiFactory.authenticated();
    const r = await api.listActiveOrders(this.activeShop.shopName);
    if (!r.success) {
      this._error = r.error ?? 'Failed to load active orders.';
      return;
    }
    this._error = null;
    this._orders = r.data ?? [];
  }

  private _open(o: IActiveOrderSummary) {
    router.navigate(
      resolveRouterPath(`orders/active/${encodeURIComponent(o.id)}`)
    );
  }

  private _nextStatus(s: SaleStatus): SaleStatus | null {
    return s === 'paid'
      ? 'preparing'
      : s === 'preparing'
        ? 'ready'
        : s === 'ready'
          ? 'completed'
          : null;
  }

  private _ctaLabel(s: SaleStatus): string {
    return s === 'paid'
      ? 'Start preparing'
      : s === 'preparing'
        ? 'Mark ready'
        : 'Complete';
  }

  // Per-card status change. Stops propagation so the card-body click
  // (navigate to detail) doesn't also fire.
  private async _changeStatus(
    o: IActiveOrderSummary,
    target: SaleStatus,
    e: Event
  ) {
    e.stopPropagation();
    if (!this.activeShop) return;
    const api = ApiFactory.authenticated();
    const r = await api.transitionSaleStatus(
      this.activeShop.shopName,
      o.id,
      target
    );
    if (!r.success) {
      this._error = r.error ?? 'Status change failed.';
      return;
    }
    await this._refresh();
  }

  // Open the cancel dialog for an order. Stops propagation so the card
  // body's navigate-to-detail click doesn't also fire.
  private _openCancel(o: IActiveOrderSummary, e: Event) {
    e.stopPropagation();
    this._cancelOrder = o;
    this._cancelRefund = true; // active orders are paid — default to giving the money back
    this._cancelMethod = 'cash';
    this._cancelReason = '';
    this._cancelSkipConfirm = false;
  }

  private _closeCancel() {
    this._cancelOrder = null;
    this._cancelSkipConfirm = false;
  }

  private async _submitCancel() {
    const o = this._cancelOrder;
    if (this._cancelBusy || !this.activeShop || !o) return;
    // Skipping the refund needs a deliberate second confirm.
    if (!this._cancelRefund && !this._cancelSkipConfirm) {
      this._cancelSkipConfirm = true;
      return;
    }
    this._cancelBusy = true;
    this._error = null;
    try {
      const api = ApiFactory.authenticated();
      const deviceId = await getOrCreateDeviceId();
      const r = await api.cancelSale(this.activeShop.shopName, o.id, {
        refundPayment: this._cancelRefund,
        refundMethod: this._cancelMethod,
        deviceId,
        reason: this._cancelReason.trim() || undefined,
      });
      if (!r.success) {
        this._error = r.error ?? 'Could not cancel the order.';
        return;
      }
      this._closeCancel();
      await this._refresh();
    } finally {
      this._cancelBusy = false;
    }
  }

  private _ageMinutes(paidUtc: string): number {
    const ms = this._now - new Date(paidUtc).getTime();
    return Math.max(0, Math.round(ms / 60000));
  }

  private _formatAge(totalMinutes: number): string {
    if (totalMinutes < 1) return '<1m';
    if (totalMinutes < 60) return `${totalMinutes}m`;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours < 24) return `${hours}h ${minutes}m`;
    const days = Math.floor(hours / 24);
    const hoursLeft = hours % 24;
    return `${days}d ${hoursLeft}h`;
  }

  private _ageBand(totalMinutes: number): 'green' | 'amber' | 'red' {
    if (totalMinutes >= this._dangerMinutes) return 'red';
    if (totalMinutes >= this._warnMinutes) return 'amber';
    return 'green';
  }

  private _displayLabel(o: IActiveOrderSummary): string {
    if (o.customerLabel && o.customerLabel !== 'Counter')
      return o.customerLabel;
    return new Date(o.paidUtc).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // The cashier's visible order number. Only rendered when the sale
  // actually got stamped with a real day sequence (Phase Y onward) —
  // legacy rows without a sequence intentionally show nothing so the
  // cashier doesn't mistake the old ULID tail for a "code". The time
  // stamp below the number row remains the universal fallback.
  private _renderOrderNumber(o: IActiveOrderSummary) {
    if (!o.daySequence || o.daySequence <= 0) return nothing;
    return html`
      <span class="order-number">
        <span class="order-number-hash">#</span>${o.daySequence}
      </span>
    `;
  }

  private _renderOrder(o: IActiveOrderSummary) {
    const age = this._ageMinutes(o.paidUtc);
    const band = this._ageBand(age);
    const next = this._nextStatus(o.status);
    const hasNumber = o.daySequence && o.daySequence > 0;
    return html`
      <sl-card class="order-card" @click=${() => this._open(o)}>
        ${
          hasNumber
            ? html`
                <div class="row">
                  ${this._renderOrderNumber(o)}
                  <span class="age ${band}">${this._formatAge(age)}</span>
                </div>
                <div class="row">
                  <strong>${this._displayLabel(o)}</strong>
                </div>
              `
            : html`
                <div class="row">
                  <strong>${this._displayLabel(o)}</strong>
                  <span class="age ${band}">${this._formatAge(age)}</span>
                </div>
              `
        }
        <div class="row">
          <small>${o.lineCount} item${o.lineCount === 1 ? '' : 's'}</small>
          <small>${o.total.toFixed(2)}</small>
        </div>
        <div class="row" style="margin-top: 8px;">
          <sl-badge
            variant=${o.source === 'guest_qr' ? 'warning' : 'neutral'}
            pill
          >
            ${o.source === 'guest_qr' ? 'QR' : 'Counter'}
          </sl-badge>
        </div>
        <div class="workflow-footer">
          <sl-button
            size="small"
            variant="default"
            class="cancel-cta"
            @click=${(e: Event) => this._openCancel(o, e)}
            >Cancel</sl-button
          >
          ${
            next
              ? html`<sl-button
                  size="small"
                  variant="primary"
                  @click=${(e: Event) => this._changeStatus(o, next, e)}
                  >${this._ctaLabel(o.status)}</sl-button
                >`
              : nothing
          }
        </div>
      </sl-card>
    `;
  }

  private _renderCancelDialog() {
    const o = this._cancelOrder;
    return html`
      <sl-dialog
        class="full-page-on-phone"
        label="Cancel order"
        ?open=${o !== null}
        @sl-request-close=${() => this._closeCancel()}
      >
        ${
          o
            ? html`
                <p>
                  Cancel order
                  ${o.daySequence > 0 ? html`<strong>#${o.daySequence}</strong>` : nothing}
                  (total <strong>${o.total.toFixed(2)}</strong>)? This takes it
                  off the board and cannot be undone.
                </p>

                <sl-switch
                  ?checked=${this._cancelRefund}
                  @sl-change=${(e: Event) => {
                    this._cancelRefund = (e.target as HTMLInputElement).checked;
                    this._cancelSkipConfirm = false;
                  }}
                  >Refund the customer ${o.total.toFixed(2)}</sl-switch
                >

                ${
                  this._cancelRefund
                    ? html`<sl-radio-group
                        label="Refund method"
                        .value=${this._cancelMethod}
                        @sl-change=${(e: Event) =>
                          (this._cancelMethod = (e.target as HTMLInputElement)
                            .value as RefundMethod)}
                      >
                        <sl-radio value="cash"
                          >Cash (returns money from the till)</sl-radio
                        >
                        <sl-radio value="card"
                          >Card (process on the card terminal)</sl-radio
                        >
                        <sl-radio value="other">Other / store credit</sl-radio>
                      </sl-radio-group>`
                    : html`<sl-alert variant="warning" open>
                        No refund will be issued — the customer's
                        ${o.total.toFixed(2)} is not returned by the system.
                      </sl-alert>`
                }

                <sl-textarea
                  label="Reason (optional)"
                  rows="2"
                  .value=${this._cancelReason}
                  @sl-input=${(e: Event) =>
                    (this._cancelReason = (
                      e.target as HTMLTextAreaElement
                    ).value)}
                ></sl-textarea>

                ${
                  this._cancelSkipConfirm
                    ? html`<sl-alert variant="danger" open
                        >Cancel <strong>without</strong> refunding
                        ${o.total.toFixed(2)}? Tap the button again to
                        confirm.</sl-alert
                      >`
                    : nothing
                }
              `
            : nothing
        }
        <sl-button slot="footer" @click=${() => this._closeCancel()}
          >Keep order</sl-button
        >
        <sl-button
          slot="footer"
          variant="danger"
          ?loading=${this._cancelBusy}
          ?disabled=${this._cancelBusy}
          @click=${() => this._submitCancel()}
          >${
            this._cancelRefund
              ? 'Cancel + refund'
              : this._cancelSkipConfirm
                ? 'Confirm cancel, no refund'
                : 'Cancel order'
          }</sl-button
        >
      </sl-dialog>
    `;
  }

  render() {
    if (this._loading) return html`<sl-card><p>Loading…</p></sl-card>`;

    const buckets: Record<
      'paid' | 'preparing' | 'ready',
      IActiveOrderSummary[]
    > = {
      paid: [],
      preparing: [],
      ready: [],
    };
    for (const o of this._orders) {
      if (
        o.status === 'paid' ||
        o.status === 'preparing' ||
        o.status === 'ready'
      ) {
        buckets[o.status].push(o);
      }
    }

    return html`
      <h2>Active orders</h2>
      ${
        this.activeShop
          ? html`<shop-day-bar
              .shopName=${this.activeShop.shopName}
              @day-changed=${() => this._refresh()}
            ></shop-day-bar>`
          : nothing
      }
      ${this._error ? html`<sl-alert variant="danger" open>${this._error}</sl-alert>` : nothing}

      <!--
        Each column is a labelled region and its title is a real heading. Three bare divs
        announced nothing at all to a screen reader, so the board read as one undifferentiated
        list of cards with no way to tell which stage a card was at - which is the entire
        information the board exists to convey.

        The aria-label deliberately omits the count. The count belongs in the visible heading,
        and a name that changed every time an order moved would make the region impossible to
        refer to stably, for assistive technology and for a test alike.
      -->
      <div class="board">
        <div class="col" role="region" aria-label="Paid orders">
          <h2 class="col-title">Paid (${buckets.paid.length})</h2>
          ${
            buckets.paid.length === 0
              ? html`<div class="empty">No new orders.</div>`
              : buckets.paid.map((o) => this._renderOrder(o))
          }
        </div>
        <div class="col" role="region" aria-label="Preparing orders">
          <h2 class="col-title">Preparing (${buckets.preparing.length})</h2>
          ${
            buckets.preparing.length === 0
              ? html`<div class="empty">Nothing in progress.</div>`
              : buckets.preparing.map((o) => this._renderOrder(o))
          }
        </div>
        <div class="col" role="region" aria-label="Ready orders">
          <h2 class="col-title">Ready (${buckets.ready.length})</h2>
          ${
            buckets.ready.length === 0
              ? html`<div class="empty">Nothing waiting.</div>`
              : buckets.ready.map((o) => this._renderOrder(o))
          }
        </div>
      </div>

      ${this._renderCancelDialog()}
    `;
  }
}
